import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarCheck2, CalendarX2, Check, Loader2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Img } from "@/components/shared/Img";
import { formWasSubmitted, markFormSubmitted } from "@/lib/assistant";
import { fmtDate, fmtPrice } from "@/lib/format";
import { cn } from "@/lib/utils";

const kr = (n) => (n == null ? "–" : fmtPrice(n));
const span = (min, max) => (min == null ? "Etter avtale" : min === max ? kr(min) : `${kr(min).replace(/\s*kr$/, "")}–${kr(max)}`);
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const cleanMessage = (m) => (m || "").replace(/\(Førespurnaden kom via assistenten på nettsida\.\)/g, "").trim();
const names = (rooms) => (rooms || []).map((r) => r.name).join(" og ");

const Shell = ({ label, children, className, testId }) => (
  <section className={cn("rounded-[16px] border border-foreground/[0.08] bg-card p-4 text-[14px] leading-snug shadow-soft", className)} data-testid={testId}>
    {label && <p className="t-mono-sm text-muted-foreground">{label}</p>}
    {children}
  </section>
);

/** Small action inside a card – sends a message on one tap. */
const Act = ({ children, onClick, testId, primary = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      primary ? "bg-ink text-paper hover:bg-sol hover:text-ink" : "border border-foreground/15 text-foreground hover:border-foreground hover:bg-foreground hover:text-background"
    )}
    data-testid={testId}
  >
    {children}
  </button>
);

/* -------------------------------------------------------------------------- rooms */
const RoomsCard = ({ card, onSend }) => (
  <Shell label="Romma våre" testId="chat-card-rooms">
    <ul className="no-scrollbar -mx-4 mt-2.5 flex snap-x gap-2.5 overflow-x-auto px-4 pb-1">
      {card.rooms.map((r) => (
        <li key={r.id} className="w-[150px] shrink-0 snap-start">
          <Link to={r.url || "/rom"} className="group block rounded-[10px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`chat-room-${r.id}`}>
            <span className="img-hover block aspect-[4/3] overflow-hidden rounded-[10px] bg-secondary">{r.image && <Img src={r.image} alt="" width={320} widths={[240, 480]} sizes="150px" className={cn("h-full w-full object-cover", r.status === "closed" && "opacity-50 grayscale")} />}</span>
            <span className={cn("mt-2 block truncate text-[13.5px] font-medium leading-tight u-hover", r.status === "closed" && "text-muted-foreground")}>{r.name.replace(/[«»]/g, "")}</span>
            <span className="block truncate text-[12px] text-muted-foreground">{[r.capacity ? `${r.capacity} pers.` : r.capacity_note, r.floor ? `${r.floor}. etg.` : null].filter(Boolean).join(" · ")}</span>
            <span className="block text-[12px] tabular">{r.status === "closed" ? <span className="text-muted-foreground">Mellombels stengt</span> : r.price_band ? `${r.price_band.single ? "" : "frå "}${kr(r.price_band.min)}` : "Etter avtale"}</span>
          </Link>
        </li>
      ))}
    </ul>
    <div className="mt-2.5 flex flex-wrap items-center gap-2">
      <Act onClick={() => onSend?.("Kva rom passar best for 80 personar til selskap?")} testId="chat-rooms-act-80">Kva passar for 80?</Act>
      <Link to="/rom" className="ml-auto inline-flex items-center gap-1 text-[13px] font-medium u-link">Alle rom og prisar <ArrowRight className="h-3 w-3" /></Link>
    </div>
  </Shell>
);

/* -------------------------------------------------------------------- availability */
const AvailabilityCard = ({ card, onSend }) => {
  const ok = card.available;
  const Icon = ok ? CalendarCheck2 : CalendarX2;
  const dateIso = card.start_date;
  return (
    <Shell testId="chat-card-availability" className={cn(ok ? "verdict-ok" : "verdict-warn")}>
      <div className="flex items-start gap-3">
        <span className={cn("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full", ok ? "bg-status-confirmed text-paper" : "bg-status-pending text-ink")}><Icon className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-[17px] font-semibold leading-tight tracking-[-0.01em]" data-testid="chat-availability-verdict">{ok ? "Ledig" : "Heilt eller delvis oppteke"}</p>
          <p className="mt-0.5 text-muted-foreground">{cap(card.date_text)}{card.start_time ? ` · kl. ${card.start_time}${card.end_time ? `–${card.end_time}` : ""}` : ""} · {names(card.rooms).replace(" og ", ", ")}</p>
          {card.closed_rooms?.length > 0 && <p className="mt-1.5 text-[13px]">Mellombels stengt: {card.closed_rooms.join(", ")}</p>}
          {card.conflicts?.length > 0 && (
            <ul className="mt-2 space-y-1 text-[13px]">
              {card.conflicts.map((c, i) => (
                <li key={i} className="flex flex-wrap gap-x-2 text-muted-foreground"><span className="text-foreground">{c.title}</span><span>{c.start_time ? `kl. ${c.start_time}${c.end_time ? `–${c.end_time}` : ""}` : "heile dagen"}</span><span>· {c.rooms.join(", ")}</span></li>
              ))}
            </ul>
          )}
          {card.recurring_same_weekday?.length > 0 && (
            <p className="mt-2 text-[13px] text-muted-foreground">Fast øving same dag: {card.recurring_same_weekday.map((p) => `${p.title} ${p.time}${p.room ? ` (${p.room})` : ""}`).join("; ")}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {ok ? (
              <Act primary onClick={() => onSend?.(`Eg vil gå vidare med ${card.date_text} i ${names(card.rooms)}. Set opp ein førespurnad.`)} testId="chat-availability-proceed">Gå vidare med denne datoen <ArrowRight className="h-3.5 w-3.5" /></Act>
            ) : (
              <Act primary onClick={() => onSend?.(`Finn ledige datoar i ${names(card.rooms)} nær ${card.date_text}.`)} testId="chat-availability-alternatives">Finn ledige datoar i nærleiken</Act>
            )}
            <Link to={`/kalender?dato=${dateIso}`} className="inline-flex items-center gap-1 text-[13px] font-medium u-link" data-testid="chat-availability-calendar-link">Kalenderen <ArrowUpRight className="h-3 w-3" /></Link>
          </div>
        </div>
      </div>
    </Shell>
  );
};

/* ---------------------------------------------------------------------- free dates */
const FreeDatesCard = ({ card, onSend }) => (
  <Shell label={`Ledige dagar · ${names(card.rooms).replace(" og ", ", ")}`} testId="chat-card-free-dates">
    {card.dates?.length ? (
      <ul className="mt-2.5 flex flex-wrap gap-2">
        {card.dates.map((d) => (
          <li key={d.date}>
            <button
              type="button"
              onClick={() => onSend?.(`Eg vil gjerne ha ${d.text} i ${names(card.rooms)}. Kan du sjekke og setje opp ein førespurnad?`)}
              className="group inline-flex h-11 items-center gap-2 rounded-[10px] border border-border bg-background px-3 text-left transition-colors hover:border-foreground hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid={`chat-free-date-${d.date}`}
              title={d.recurring?.length ? `Fast øving: ${d.recurring.join(", ")}` : "Heilt ledig"}
            >
              <span className="font-display text-[19px] font-semibold tabular leading-none">{fmtDate(d.date, "d")}</span>
              <span className="flex flex-col leading-none">
                <span className="text-[12.5px] font-medium">{fmtDate(d.date, "MMM").replace(".", "")}</span>
                <span className="mt-0.5 text-[10.5px] uppercase tracking-[0.1em] opacity-70">{d.weekday.slice(0, 3)}</span>
              </span>
              {d.recurring?.length > 0 && <span className="h-1.5 w-1.5 rounded-full bg-sol" aria-label="Fast øving same dag" />}
            </button>
          </li>
        ))}
      </ul>
    ) : <p className="mt-2 text-muted-foreground">Ingen heilt ledige dagar i perioden.</p>}
    <p className="mt-2.5 text-[12.5px] text-muted-foreground">Trykk på ein dato for å gå vidare. Gul prikk = fast øving same dag.</p>
  </Shell>
);

/* ------------------------------------------------------------------------ estimate */
const EstimateCard = ({ card, onSend }) => (
  <Shell label={`Rettleiande pris · ${card.event_type || ""}`} testId="chat-card-estimate">
    <p className="mt-1.5 font-display text-[26px] font-semibold leading-none tracking-[-0.03em] tabular" data-testid="chat-estimate-total">{span(card.min, card.max)}</p>
    <ul className="mt-3 space-y-1.5 border-t border-border pt-2.5 text-[13px]">
      {card.rooms.map((r) => (
        <li key={r.room_id} className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="font-medium">{r.room_name}</span>
            {r.lines?.length > 0 && <span className="block text-muted-foreground">{r.lines.map((l) => l.label).join(" / ")}</span>}
            {r.note && <span className="block text-muted-foreground">{r.note}</span>}
          </span>
          <span className="shrink-0 tabular">{span(r.min, r.max)}</span>
        </li>
      ))}
    </ul>
    <p className="mt-2.5 text-[12.5px] text-muted-foreground">{card.excludes_cleaning ? "Eksklusiv vask, tilrigging og rydding. " : ""}Styret stadfestar endeleg pris. <Link to="/prisar" className="u-link">Heile prislista</Link></p>
    <div className="mt-3 flex flex-wrap gap-2">
      <Act primary onClick={() => onSend?.(`Finn ledige datoar for ${(card.event_type || "dette").toLowerCase()} i ${card.rooms.map((r) => r.room_name).join(" og ")}.`)} testId="chat-estimate-dates">Finn ledige datoar</Act>
      <Act onClick={() => onSend?.("Kva er inkludert i prisen, og kva kjem i tillegg?")} testId="chat-estimate-included">Kva er inkludert?</Act>
    </div>
  </Shell>
);

/* -------------------------------------------------------------------------- events */
const EventsCard = ({ card }) => (
  <Shell label="I programmet" testId="chat-card-events">
    {card.events?.length ? (
      <ul className="mt-2 divide-y divide-border">
        {card.events.map((e) => (
          <li key={e.id}>
            <Link to={e.url || `/arrangement/${e.id}`} className="group -mx-1.5 flex items-center gap-3 rounded-[10px] px-1.5 py-2 transition-colors hover:bg-secondary/70" data-testid={`chat-event-${e.id}`}>
              <span className="w-9 shrink-0 text-center"><span className="block font-display text-[20px] font-semibold leading-none tabular">{fmtDate(e.date, "d")}</span><span className="t-mono-sm text-muted-foreground">{fmtDate(e.date, "MMM").replace(".", "")}</span></span>
              <span className="min-w-0 flex-1"><span className="block truncate font-medium">{e.title}</span><span className="block truncate text-[12.5px] text-muted-foreground">{[cap(e.date_text?.split(" ")[0]), e.start_time ? `kl. ${e.start_time}` : null, e.location].filter(Boolean).join(" · ")}</span></span>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </Link>
          </li>
        ))}
      </ul>
    ) : <p className="mt-2 text-muted-foreground">Ingen komande arrangement er publiserte akkurat no.</p>}
    <Link to="/arrangement" className="mt-2.5 inline-flex items-center gap-1 text-[13px] font-medium u-link">Heile programmet <ArrowRight className="h-3 w-3" /></Link>
  </Shell>
);

/* -------------------------------------------------------------------- contact form */
const FIELD = {
  name: { label: "Namn", type: "text", autoComplete: "name", placeholder: "Kari Nordmann", required: true },
  email: { label: "E-post", type: "email", autoComplete: "email", placeholder: "kari@example.no", required: true, inputMode: "email" },
  phone: { label: "Telefon", type: "tel", autoComplete: "tel", placeholder: "900 00 000", required: true, inputMode: "tel" },
  guests: { label: "Tal personar", type: "number", autoComplete: "off", placeholder: "80", required: false, inputMode: "numeric", min: 1, max: 1000 },
  organization: { label: "Organisasjon (valfritt)", type: "text", autoComplete: "organization", placeholder: "Lag, bedrift …", required: false },
};
const SENTENCE = { name: "Namn", email: "E-post", phone: "Telefon", guests: "Tal personar", organization: "Organisasjon" };

const ContactFormCard = ({ card, onSend, disabled }) => {
  const [vals, setVals] = useState({});
  const [errs, setErrs] = useState({});
  const [done, setDone] = useState(() => formWasSubmitted(card.id));
  const fields = (card.fields || ["name", "email", "phone"]).filter((f) => FIELD[f]);

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    fields.forEach((f) => {
      const v = (vals[f] || "").trim();
      if (FIELD[f].required && !v) next[f] = "Må fyllast ut";
      else if (f === "email" && v && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) next[f] = "Skriv ei gyldig e-postadresse";
      else if (f === "phone" && v && v.replace(/\D/g, "").length < 8) next[f] = "Skriv minst 8 siffer";
      else if (f === "name" && v && v.length < 2) next[f] = "Minst 2 teikn";
      else if (f === "guests" && v && (Number(v) < 1 || Number(v) > 1000)) next[f] = "Mellom 1 og 1000";
    });
    setErrs(next);
    if (Object.keys(next).length) return;
    const parts = fields.filter((f) => (vals[f] || "").trim()).map((f) => `${SENTENCE[f]}: ${vals[f].trim()}`);
    markFormSubmitted(card.id);
    setDone(true);
    onSend?.(`Her er kontaktinfoen min – ${parts.join(". ")}.`);
  };

  if (done) {
    return (
      <Shell className="verdict-muted" testId="chat-card-contact-form">
        <p className="flex items-center gap-2 text-[13.5px] text-muted-foreground"><Check className="h-4 w-4 text-status-confirmed" />Kontaktinfoen er lagt til i samtalen.</p>
      </Shell>
    );
  }
  return (
    <Shell label="Kontaktinfo til førespurnaden" testId="chat-card-contact-form" className="border-foreground/20 bg-card">
      {card.note && <p className="mt-1 text-[13.5px] text-muted-foreground">{card.note}</p>}
      <form onSubmit={submit} noValidate className="mt-3 grid gap-3 sm:grid-cols-2">
        {fields.map((f) => {
          const d = FIELD[f];
          const wide = f === "organization";
          return (
            <div key={f} className={cn("grid gap-1.5", wide && "sm:col-span-2")}>
              <Label htmlFor={`hv-${card.id}-${f}`} className="text-[12.5px] text-muted-foreground">{d.label}</Label>
              <Input
                id={`hv-${card.id}-${f}`}
                name={f}
                type={d.type}
                inputMode={d.inputMode}
                autoComplete={d.autoComplete}
                placeholder={d.placeholder}
                min={d.min}
                max={d.max}
                required={d.required}
                aria-invalid={Boolean(errs[f])}
                value={vals[f] || ""}
                onChange={(e) => setVals((v) => ({ ...v, [f]: e.target.value }))}
                className={cn("h-11 bg-background text-[15px]", errs[f] && "border-destructive")}
                data-testid={`chat-form-${f}`}
              />
              {errs[f] && <p className="text-[12px] text-destructive" role="alert">{errs[f]}</p>}
            </div>
          );
        })}
        <div className="sm:col-span-2 flex flex-wrap items-center gap-3 pt-1">
          <Button type="submit" disabled={disabled} className="h-11 px-5" data-testid="chat-form-submit">Bruk desse opplysningane <ArrowRight className="h-4 w-4" /></Button>
          <p className="text-[12px] text-muted-foreground">Vert berre brukt i førespurnaden til styret.</p>
        </div>
      </form>
    </Shell>
  );
};

/* ------------------------------------------------------------------- booking draft */
const Row = ({ k, v, testId }) => (v == null || v === "" ? null : (
  <div className="grid grid-cols-[88px_1fr] gap-x-3 py-1.5"><dt className="text-muted-foreground">{k}</dt><dd className="min-w-0 break-words font-medium" data-testid={testId}>{v}</dd></div>
));

const DraftCard = ({ card, state, result, busy, onConfirm, onCancel }) => {
  const status = state || card.state || "pending";
  const est = card.estimate || {};
  return (
    <Shell className={cn("border-foreground/20 bg-card", status === "sent" && "verdict-ok")} testId="chat-card-draft">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="t-mono-sm text-muted-foreground">{status === "sent" ? "Førespurnad sendt" : status === "cancelled" ? "Utkast avbrote" : "Sjekk før du sender"}</p>
          <p className="mt-1 font-display text-[18px] font-semibold leading-tight tracking-[-0.015em]">{card.event_title || card.event_type}{card.guests ? ` · ${card.guests} personar` : ""}</p>
        </div>
        {status === "sent" && <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-status-confirmed text-paper"><Check className="h-4 w-4" /></span>}
      </div>
      <dl className="mt-3 divide-y divide-border border-y border-border text-[13.5px]">
        <Row k="Rom" v={card.rooms?.join(" + ")} testId="chat-draft-rooms" />
        <Row k="Dato" v={cap(card.date_text)} testId="chat-draft-date" />
        <Row k="Tid" v={card.time} />
        <Row k="Føremål" v={`${card.purpose}${card.event_type ? ` · ${card.event_type}` : ""}`} />
        <Row k="Kontakt" v={[card.name, card.organization].filter(Boolean).join(", ")} testId="chat-draft-name" />
        <Row k="E-post" v={card.email} />
        <Row k="Telefon" v={card.phone} />
        {card.extras?.length > 0 && <Row k="Tillegg" v={card.extras.join(", ")} />}
        <Row k="Prisgrunnlag" v={<span><span className="tabular" data-testid="chat-draft-estimate">{span(est.min, est.max)}</span><span className="block text-[12.5px] font-normal text-muted-foreground">Rettleiande{est.excludes_cleaning ? ", eksklusiv vask" : ""} – styret stadfestar endeleg pris.</span></span>} />
        {cleanMessage(card.message) && <Row k="Melding" v={<span className="whitespace-pre-line font-normal">{cleanMessage(card.message)}</span>} />}
      </dl>
      {card.has_conflict && status !== "sent" && (
        <p className="mt-3 rounded-[10px] border verdict-warn px-3 py-2 text-[13px]" data-testid="chat-draft-conflict">Tida er heilt eller delvis oppteken i kalenderen. Du kan sende førespurnaden likevel – styret vurderer om det går.</p>
      )}
      {card.warnings?.filter((w) => w).map((w, i) => <p key={i} className="mt-2 text-[13px] text-muted-foreground">{w}</p>)}

      {status === "sent" ? (
        <div className="mt-3 text-[13.5px]" data-testid="chat-draft-sent">
          <p className="font-medium">Motteken – ventar på styret.</p>
          <p className="mt-1 text-muted-foreground">Ikkje bindande. Styret svarar på e-post{result?.id ? ` · referanse ${String(result.id).slice(0, 8)}` : ""}.</p>
        </div>
      ) : status === "cancelled" ? (
        <p className="mt-3 text-[13.5px] text-muted-foreground" data-testid="chat-draft-cancelled">Ingenting er sendt. Skriv kva du vil endre, så lagar eg eit nytt utkast.</p>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button onClick={onConfirm} disabled={busy} className="h-11 px-5" data-testid="chat-draft-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}{busy ? "Sender …" : "Send førespurnad"}
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={busy} className="h-11 px-4" data-testid="chat-draft-cancel"><Undo2 className="h-4 w-4" />Endre noko</Button>
          <p className="basis-full text-[12.5px] text-muted-foreground">Uforpliktande førespurnad. Inga betaling. Styret stadfestar på e-post.</p>
        </div>
      )}
    </Shell>
  );
};

/** Renders one grounded card from the assistant stream. `onSend` sends a message as the visitor. */
export const AssistantCard = ({ card, draftState, onSend, onConfirmDraft, onCancelDraft, busy, disabled }) => {
  switch (card?.type) {
    case "rooms": return <RoomsCard card={card} onSend={onSend} />;
    case "availability": return <AvailabilityCard card={card} onSend={onSend} />;
    case "free_dates": return <FreeDatesCard card={card} onSend={onSend} />;
    case "estimate": return <EstimateCard card={card} onSend={onSend} />;
    case "events": return <EventsCard card={card} />;
    case "contact_form": return <ContactFormCard card={card} onSend={onSend} disabled={disabled} />;
    case "booking_draft": return (
      <DraftCard
        card={card}
        state={draftState?.status}
        result={draftState?.result}
        busy={busy}
        onConfirm={() => onConfirmDraft?.(card.draft_id)}
        onCancel={() => onCancelDraft?.(card.draft_id)}
      />
    );
    default: return null;
  }
};

/** Deterministic, grounded follow-ups shown under the latest answer – never invented by the model. */
export const followUpsFor = (msg, drafts) => {
  const cards = msg?.cards || [];
  const types = new Set(cards.map((c) => c.type));
  const draft = cards.find((c) => c.type === "booking_draft");
  if (draft && (drafts?.[draft.draft_id]?.status || draft.state) === "pending") return [];
  if (types.has("contact_form")) return [];
  if (types.has("free_dates")) return ["Kva kostar det?", "Vis andre helger"];
  if (types.has("availability")) return [];
  if (types.has("estimate")) return ["Finn ledige datoar", "Set opp ein førespurnad"];
  if (types.has("rooms")) return ["Kva passar for 80 personar?", "Kva er ledig i helgene?"];
  if (types.has("events")) return ["Korleis kjøper eg billettar?", "Kan eg arrangere noko sjølv?"];
  if (draft) return ["Takk, det var alt", "Eg vil booke noko meir"];
  return [];
};
